<template>
  <v-dialog
    v-model="dialog"
    max-width="1100"
    :persistent="loading"
    scrollable
    :fullscreen="$vuetify.breakpoint.smAndDown"
  >
    <v-card>
      <v-card-title>Gerar funcionalidade com IA</v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="5">
            <!-- idle: briefing -->
            <template v-if="step === 'idle'">
              <v-textarea
                v-model="brief"
                label="Descreva a funcionalidade"
                outlined
                dense
                rows="10"
                :disabled="loading"
                hint="Explique o que a função faz, para quem é, principais benefícios e diferenciais."
                persistent-hint
              />
              <v-btn
                color="primary"
                class="mt-3"
                :loading="loading"
                :disabled="loading || !brief?.trim()"
                @click="start"
              >
                <v-icon left>mdi-robot</v-icon>
                Continuar
              </v-btn>
            </template>

            <!-- questions -->
            <template v-else-if="step === 'questions'">
              <div class="text-subtitle-2 mb-3">
                {{ suggestion ? "Perguntas sobre os ajustes" : "Perguntas da IA" }}
              </div>
              <FeatureAiQuestionForm
                ref="questionForm"
                :questions="questions"
                :disabled="loading"
              />
              <div class="d-flex flex-wrap mt-3" style="gap: 8px">
                <v-btn
                  color="primary"
                  :loading="loading"
                  :disabled="loading"
                  @click="submitAnswers"
                >
                  Enviar respostas
                </v-btn>
                <v-btn
                  v-if="!suggestion"
                  outlined
                  color="primary"
                  :loading="loading"
                  :disabled="loading"
                  @click="skipAndGenerate"
                >
                  Pular perguntas e gerar
                </v-btn>
              </div>
            </template>

            <!-- complete -->
            <template v-else-if="step === 'complete'">
              <v-alert type="success" dense text class="mb-3">
                Sugestão gerada com sucesso. Revise a exibição e aceite se estiver ok.
              </v-alert>
              <v-textarea
                v-model="refineFeedback"
                label="O que deseja mudar?"
                outlined
                dense
                rows="3"
                :disabled="loading"
                hint="Descreva os ajustes. A IA pode fazer perguntas antes de atualizar."
                persistent-hint
                class="mb-3"
              />
              <v-btn
                color="primary"
                class="mb-3 mr-2"
                :loading="loading"
                :disabled="loading || !refineFeedback?.trim()"
                @click="submitRefine"
              >
                <v-icon left>mdi-tune</v-icon>
                Aplicar ajustes
              </v-btn>
              <v-btn
                outlined
                color="primary"
                class="mb-3"
                :loading="loading"
                :disabled="loading"
                @click="skipAndGenerate"
              >
                <v-icon left>mdi-refresh</v-icon>
                Regenerar
              </v-btn>
              <v-expansion-panels flat>
                <v-expansion-panel>
                  <v-expansion-panel-header>Ver campos gerados</v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div class="text-caption grey--text mb-1">Slug</div>
                    <div class="mb-3">{{ suggestion.slug }}</div>
                    <div class="text-caption grey--text mb-1">Ícone</div>
                    <div class="mb-3 d-flex align-center">
                      <v-icon class="mr-2">{{ suggestion.icon }}</v-icon>
                      {{ suggestion.icon }}
                    </div>
                    <div class="text-caption grey--text mb-1">
                      Benefícios ({{ suggestion.mainBenefits?.length || 0 }})
                    </div>
                    <div class="mb-3">
                      {{ (suggestion.mainBenefits || []).map((b) => b.title).join(", ") || "—" }}
                    </div>
                    <div class="text-caption grey--text mb-1">
                      FAQ ({{ suggestion.faq?.length || 0 }})
                    </div>
                    <div>
                      {{ (suggestion.faq || []).map((f) => f.question).join("; ") || "—" }}
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </template>

            <v-alert v-if="error" type="error" dense class="mt-4">
              {{ error }}
            </v-alert>
          </v-col>

          <v-col cols="12" md="7">
            <div class="text-subtitle-2 mb-2">Visualização</div>
            <div
              v-if="!suggestion"
              class="grey lighten-4 rounded-lg pa-8 text-center"
            >
              <v-icon size="48" color="grey">mdi-robot-outline</v-icon>
              <p class="mt-3 mb-0 grey--text">
                <template v-if="step === 'idle'">
                  Descreva a funcionalidade e clique em Continuar.
                </template>
                <template v-else>
                  Responda às perguntas para a IA gerar a sugestão.
                </template>
              </p>
            </div>
            <FeatureDisplay v-else embedded :value="displaySuggestion" />
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn text :disabled="loading" @click="cancel">Cancelar</v-btn>
        <v-spacer />
        <v-btn
          v-if="step === 'questions'"
          text
          color="primary"
          :disabled="loading"
          @click="skipAndGenerate"
        >
          Pular e gerar
        </v-btn>
        <v-btn
          color="primary"
          :disabled="loading || step !== 'complete' || !suggestion"
          @click="accept"
        >
          Aceitar sugestão
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import featuresService from "@/services/ultimate/features";
import FeatureDisplay from "@/components/ultimate/feature/FeatureDisplay.vue";
import FeatureAiQuestionForm from "@/components/ultimate/feature/FeatureAiQuestionForm.vue";

export default {
  name: "FeatureAiGenerate",
  components: { FeatureDisplay, FeatureAiQuestionForm },
  data() {
    return {
      dialog: false,
      loading: false,
      error: null,
      step: "idle",
      brief: "",
      sessionId: null,
      questions: [],
      suggestion: null,
      context: null,
      audiences: [],
      formFeature: null,
      refineFeedback: "",
      resolve: null,
    };
  },
  computed: {
    displaySuggestion() {
      if (!this.suggestion) return {};
      const base = this.formFeature || {};
      return {
        ...this.suggestion,
        path: base.path || [],
        screenshots: base.screenshots || [],
        links: base.links || [],
        ctaLabel: this.suggestion.ctaLabel || base.ctaLabel || "",
        ctaUrl: base.ctaUrl || "",
        status: base.status || this.context?.status || "AVAILABLE",
        audiences: this.audiences,
      };
    },
  },
  methods: {
    open({ feature, audiences } = {}) {
      this.dialog = true;
      this.step = "idle";
      this.brief = feature?.description || "";
      this.sessionId = null;
      this.questions = [];
      this.suggestion = null;
      this.error = null;
      this.formFeature = feature ? { ...feature } : null;
      this.context = feature
        ? { title: feature.title, slug: feature.slug, status: feature.status }
        : null;
      this.audiences = audiences || [];
      return new Promise((resolve) => {
        this.resolve = resolve;
      });
    },
    cancel() {
      if (this.resolve) this.resolve(false);
      this.resolve = null;
      this.dialog = false;
      this.reset();
    },
    accept() {
      if (this.resolve) this.resolve(this.suggestion);
      this.resolve = null;
      this.dialog = false;
      this.reset();
    },
    reset() {
      this.step = "idle";
      this.brief = "";
      this.sessionId = null;
      this.questions = [];
      this.suggestion = null;
      this.error = null;
      this.formFeature = null;
      this.refineFeedback = "";
      this.context = null;
      this.audiences = [];
    },
    formatAiError(error) {
      const message = error?.message || "";
      if (message.includes("IA deve usar uma ferramenta")) {
        return "A IA não seguiu o fluxo esperado. Clique em Continuar novamente para tentar de novo.";
      }
      return message || "Erro ao processar resposta da IA";
    },
    handleChatResponse(response) {
      this.sessionId = response.sessionId;
      if (response.status === "questions") {
        this.step = "questions";
        this.questions = response.questions || [];
        return;
      }
      if (response.status === "complete") {
        this.step = "complete";
        this.suggestion = response.feature;
        this.questions = [];
        this.refineFeedback = "";
      }
    },
    async start() {
      if (!this.brief?.trim()) return;
      try {
        this.loading = true;
        this.error = null;
        const response = await featuresService.aiChatStart({
          brief: this.brief,
          context: this.context,
        });
        this.handleChatResponse(response);
      } catch (error) {
        this.error = this.formatAiError(error);
      } finally {
        this.loading = false;
      }
    },
    async submitAnswers() {
      if (!this.$refs.questionForm?.validate()) {
        this.error = "Responda todas as perguntas antes de continuar.";
        return;
      }
      try {
        this.loading = true;
        this.error = null;
        const answers = this.$refs.questionForm.buildAnswers();
        const response = await featuresService.aiChatAnswer({
          sessionId: this.sessionId,
          answers,
        });
        this.handleChatResponse(response);
      } catch (error) {
        this.error = this.formatAiError(error);
      } finally {
        this.loading = false;
      }
    },
    async skipAndGenerate() {
      if (!this.sessionId && this.step === "idle") {
        await this.start();
        if (this.step === "questions") {
          await this.doSkip();
        }
        return;
      }
      await this.doSkip();
    },
    async doSkip() {
      if (!this.sessionId) return;
      try {
        this.loading = true;
        this.error = null;
        const response = await featuresService.aiChatSkip({
          sessionId: this.sessionId,
        });
        this.handleChatResponse(response);
      } catch (error) {
        this.error = this.formatAiError(error);
      } finally {
        this.loading = false;
      }
    },
    async submitRefine() {
      if (!this.refineFeedback?.trim() || !this.sessionId) return;
      try {
        this.loading = true;
        this.error = null;
        const response = await featuresService.aiChatRefine({
          sessionId: this.sessionId,
          feedback: this.refineFeedback.trim(),
        });
        this.handleChatResponse(response);
      } catch (error) {
        this.error = this.formatAiError(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
